import { useMemo, useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CTA_OUTLINE, CtaStyles, ctaOverPhoto } from './_cta';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy by
 * EXPANDING the range into every step: "Studio - 2" → "Studio, 1 & 2 Bedroom",
 * "1 - 3" → "1, 2 & 3 Bedroom". Single values ("1" → "1 Bedroom") and
 * studios ("studio" → "Studio") pass through, and equal endpoints collapse
 * to a single value ("2 - 2" → "2 Bedroom", "Studio - Studio" → "Studio").
 * Unparseable endpoints (e.g. "1 - 3 + den") fall back to the raw dash range.
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);

  // Normalize an endpoint to a bedroom count (studio = 0), or null if it
  // isn't a recognizable whole-number count.
  const toCount = (p: string): number | null => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 0;
    const n = Number(lower);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };
  const countLabel = (n: number): string => (n === 0 ? 'Studio' : String(n));

  // Expand a two-endpoint range into every step in between.
  let counts: number[] | null = null;
  if (parts.length === 2) {
    const lo = toCount(parts[0]);
    const hi = toCount(parts[1]);
    if (lo !== null && hi !== null && lo <= hi) {
      counts = [];
      for (let n = lo; n <= hi; n++) counts.push(n);
    }
  } else if (parts.length === 1) {
    const only = toCount(parts[0]);
    if (only !== null) counts = [only];
  }

  if (counts) {
    const labels = counts.map(countLabel);
    // Render as "A, B & C" (single item stays as-is).
    const label =
      labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
    const allStudios = labels.every((p) => p === 'Studio');
    return allStudios ? label : `${label} Bedroom`;
  }

  // Fallback for unparseable endpoints: keep the raw dash range.
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;
  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Floor Plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  // Arch-reveal refs: the section is taller than the viewport and pins a
  // sticky stage; scrolling through the extra height drives the arch open,
  // then a second phase parallaxes the copy up over the pinned image.
  const sectionRef = useRef<HTMLElement>(null);
  const archRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const arch = archRef.current;
    const content = contentRef.current;
    if (!section || !arch) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The scroll runway is split into two phases:
    //   • phase 1 (0 → OPEN_FRACTION): the arch opens from portal to full-bleed
    //   • phase 2 (OPEN_FRACTION → 1): the image stays pinned while the copy
    //     parallaxes upward and fades, so it drifts off before the next section
    const OPEN_FRACTION = 0.5;
    // Curvature of the arched top, expressed as vertical-radius ÷ horizontal-
    // radius. This ratio is held CONSTANT for the whole animation and at every
    // viewport size (see `apply`), which is what keeps the dome consistently
    // rounded instead of stretching into a tall oval as the arch expands.
    // 1.0 would be a pure semicircle; a little over 1 gives a gently domed arch
    // that still reads as round rather than pointed.
    const ARCH_RATIO = 1.12;

    // Closed-state (p=0) geometry adapts continuously to viewport width: a
    // narrow, tall portal on desktop, widening and flattening toward mobile so
    // it never pinches into a tight gothic spike on a phone. Interpolating
    // across a band (rather than snapping at a single breakpoint) means tablets
    // land naturally in between and a resize across the range never "pops".
    // Recomputed each frame so orientation / resize changes are picked up.
    // Keep the endpoints roughly in sync with the .hero-arch CSS below (used
    // for SSR / first paint before this effect runs).
    const closedGeom = () => {
      // t: 0 at small phones (<=480px) → 1 at desktop (>=1024px).
      const t = clamp((window.innerWidth - 480) / (1024 - 480), 0, 1);
      return {
        w: lerp(92, 58, t), // wide on phones, narrow portal on desktop
        // Shorter on phones so the arch's top starts lower and clears the fixed
        // header/logo (the arch is anchored to the viewport bottom, so a smaller
        // height pushes its rounded top down away from the header band).
        h: lerp(78, 70, t),
      };
    };

    // Apply the arch geometry for a given open-progress p (0 = closed/inset
    // arch, 1 = fully open near-full-bleed panel that keeps a soft rounded top).
    const apply = (p: number) => {
      const g = closedGeom();
      const wPct = lerp(g.w, 100, p);
      const hPct = lerp(g.h, 100, p);
      arch.style.width = `${wPct}%`;
      arch.style.height = `${hPct}%`;

      // Drive the border-radius in PIXELS, not percentages. Percentage radii
      // are relative to the element's own box, so as the arch grew toward
      // full-bleed its vertical radius (a % of the tall viewport) ballooned far
      // past its horizontal radius and the top stretched into a pointed oval.
      // Instead: the horizontal radius is always HALF the arch's width, so the
      // two top corners meet exactly in the middle → one continuous arch curve
      // (never a flat edge); and the vertical radius is a fixed multiple of
      // that horizontal radius, so the dome keeps the same rounded curvature at
      // every size and every animation frame. Clamped so it can't exceed the
      // element's own height at the tightest (closed) sizes.
      const wPx = (wPct / 100) * window.innerWidth;
      const hPx = (hPct / 100) * window.innerHeight;
      const rHpx = wPx / 2;
      const rVpx = Math.min(rHpx * ARCH_RATIO, hPx);
      arch.style.borderTopLeftRadius = `${rHpx}px ${rVpx}px`;
      arch.style.borderTopRightRadius = `${rHpx}px ${rVpx}px`;
    };

    // Parallax the copy upward (and fade it) during phase 2. The image itself
    // stays pinned (it lives in the sticky, full-bleed arch), so only the text
    // moves — a foreground-over-fixed-background parallax.
    //
    // Travel scales with `distance` — the phase-2 scroll runway in px — rather
    // than a fixed fraction of the viewport. That keeps the scroll-to-travel
    // RATIO (0.7) constant across viewports, so the copy drifts up at the same
    // relative speed on mobile as on desktop even though the mobile section
    // (and therefore its runway) is much shorter. A fixed-vh shift made mobile
    // shoot up nearly twice as fast as the scroll — an abrupt jump instead of
    // the gentle desktop drift; this makes the effect read identically on both.
    const applyParallax = (q: number, distance: number) => {
      if (!content) return;
      const shift = q * distance * 0.7; // px travelled upward
      content.style.transform = `translate3d(0, ${-shift}px, 0)`;
      content.style.opacity = `${clamp(1 - q * 1.15, 0, 1)}`;
    };

    // Reduced motion (or non-scroll contexts): show the open state immediately.
    if (reduce) {
      apply(1);
      applyParallax(0, 0);
      return;
    }

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // Distance we can scroll while the sticky stage is pinned.
      const total = section.offsetHeight - window.innerHeight;
      const p = total > 0 ? clamp(-rect.top / total, 0, 1) : 1;
      // Phase 1: open the arch. Phase 2: hold it open and parallax the copy.
      apply(clamp(p / OPEN_FRACTION, 0, 1));
      // Phase-2 runway (px) drives the parallax travel so its speed relative to
      // scrolling is the same on every viewport regardless of section height.
      const phase2Distance = total * (1 - OPEN_FRACTION);
      applyParallax(clamp((p - OPEN_FRACTION) / (1 - OPEN_FRACTION), 0, 1), phase2Distance);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll(); // seed on mount (handles pre-scrolled pages)
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    // The section is taller than the viewport so the sticky stage stays
    // pinned while the arch "opens". The dark backdrop is what the arch is
    // revealed against (matches the header's dark band above it).
    <section ref={sectionRef} className="hero-section relative w-full bg-[var(--vibe-color-primary)]">
      {/* Anchor the arch to the BOTTOM of the stage (items-end) so its rounded
          top starts well below the fixed header and its base sits flush with
          the viewport bottom — matching the reference. As it opens it grows
          upward to fill the screen. */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-end justify-center">
        {/* Arched portal: starts inset with a rounded top, expands to full-bleed
            on scroll. Width / height / radius are driven imperatively in the
            effect above; the inline values here are the closed (p=0) state so
            SSR + first paint are correct before JS runs. */}
        <div
          ref={archRef}
          className="hero-arch relative overflow-hidden"
          style={{
            // Closed-state geometry (width / height / border-radius) lives in
            // CSS (.hero-arch) so it can vary by breakpoint — a narrow tall
            // portal on desktop, a wide gentle arch on mobile. The scroll
            // effect below overrides these inline as the portal opens.
            // Translucent "portal frame" tracing the arch outline (the
            // transparent element in the reference). Layered box-shadows follow
            // the rounded border-radius automatically and ride along as the
            // corners flatten during the reveal:
            //   1. crisp light edge right at the image border
            //   2. a wider translucent light band against the dark backdrop
            //   3. a soft dark fade blending the band back into the backdrop
            //   4. depth shadow lifting the portal off the page
            // The band / blur / lift sizes are clamp()'d CSS vars (defined on
            // .hero-arch) so the framing scales with the viewport instead of
            // staying a fixed pixel weight that looks heavy on phones and thin
            // on large displays.
            boxShadow: [
              '0 0 0 1.5px rgba(255, 255, 255, 0.22)',
              '0 0 0 var(--hero-band) rgba(255, 255, 255, 0.06)',
              '0 0 var(--hero-blur) var(--hero-blur-spread) rgba(0, 0, 0, 0.45)',
              '0 var(--hero-lift-y) var(--hero-lift-blur) rgba(0, 0, 0, 0.55)',
            ].join(', '),
            willChange: 'width, height, border-radius',
          }}
        >
          {/* Background image, clipped by the arch. It's sized to the FULL
              viewport (w-screen/h-screen) and anchored bottom-center — NOT to
              the arch box — so it stays a fixed size while the arch opens. The
              arch's overflow-hidden acts as a growing window: expanding the
              arch reveals more of the same static image rather than re-fitting
              (and therefore zooming) it as the box grows. Because the arch is
              itself centered and bottom-flush with the viewport, anchoring the
              image bottom-center keeps it locked to the viewport at every arch
              size. */}
          <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-screen h-screen">
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  sizes="100vw"
                  className="w-full h-full object-cover"
                  style={{
                    imageRendering: 'high-quality' as any,
                  }}
                />
              ) : (
                <div className="w-full h-full bg-black" />
              )}
            </div>
          </EditableSlotGroup>

          {/* Gradient overlay for text contrast — purely decorative, must not
              intercept clicks so floating widgets above the hero stay usable. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10 pointer-events-none" />

          {/* Inner frame edge: a translucent ring in the primary theme color
              that overlaps the IMAGE edge so the frame reads as a tinted
              semi-transparent overlay over the photo (the photo shows faintly
              through it) — matching the reference. color-mix applies the alpha
              to the primary token so it stays theme-driven; borderRadius
              'inherit' locks it to the arch shape as the corners flatten. The
              width is a clamp()'d viewport-relative var so it stays visually
              proportional from phone to desktop. */}
          <div
            className="absolute inset-0 z-[5] pointer-events-none"
            style={{
              borderRadius: 'inherit',
              border:
                'var(--hero-frame) solid color-mix(in srgb, var(--vibe-color-primary) 55%, transparent)',
            }}
          />

          {/* Content sits in the lower portion of the arch (justify-end +
              bottom padding) to match the reference. The wrapper ignores
              pointer events; only the interactive children re-enable them, so
              empty space passes clicks through to anything floating on top. */}
          {/* Anchor the copy a fixed distance above the viewport bottom using a
              viewport-relative unit (vh). The arch's base is always flush with
              the viewport bottom, and vh doesn't change as the arch opens — so
              the title/subtitle/CTA stay put while scrolling, instead of the
              old pb-[12%] which grew with the arch's animating width. */}
          <div
            ref={contentRef}
            className="absolute inset-0 z-10 flex flex-col items-center justify-end pb-[20vh] md:pb-[12vh] text-center text-white px-6 sm:px-8 pointer-events-none"
            style={{ willChange: 'transform, opacity' }}
          >
            <RichTextContent
              path="hero.title"
              label="Title"
              defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
              as="h1"
              className="hero-entrance hero-entrance-2 hero-title-glow font-heading text-[clamp(1.75rem,8vw,2.5rem)] sm:text-5xl md:text-6xl font-bold mb-4 max-w-3xl leading-[1.1] tracking-tight drop-shadow-lg pointer-events-auto"
            />

            <RichTextContent
              path="hero.subtitle"
              label="Subtitle"
              defaultValue={subtitleHtml}
              as="p"
              className="hero-entrance hero-entrance-3 font-body text-[clamp(0.9rem,3.8vw,1.125rem)] sm:text-lg md:text-xl max-w-[74vw] sm:max-w-2xl mb-8 leading-relaxed opacity-90 drop-shadow-md pointer-events-auto"
            />

            <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
              {/* Matches the footer's "Apply Now" treatment: a semi-transparent
                  outline button that fills on hover and flips its label to the
                  primary brand color. Tuned to white here (instead of the
                  footer's text-on-primary token) because this CTA sits over the
                  dark hero photo where white reads correctly regardless of the
                  property's primary color. */}
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className={`hero-entrance hero-entrance-4 pointer-events-auto hero-cta ${CTA_OUTLINE}`}
                style={ctaOverPhoto}
              >
                {ctaLink.label || 'Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <CtaStyles />

      <style>{`
        /* The Hero CTA is the ONE exception that keeps caps — it sits over the
           hero photo and reads as the primary call-to-action. Its size comes
           from the canonical .v-cta (see _cta.tsx), shared by every CTA in the
           theme; the only Hero-specific override is the uppercase treatment.
           Every other CTA renders in its label's natural case. */
        .hero-cta.v-cta {
          text-transform: uppercase;
        }
        /* Overall section height. It's taller than the viewport so the sticky
           stage stays pinned while the arch opens on scroll; the extra height
           IS the scroll runway. The runway is now split in two — the first half
           opens the arch, the second half parallaxes the copy up over the
           pinned image — so the section is taller than before to give phase 2
           its own scroll distance. Mobile keeps a shorter runway so the hero
           doesn't feel excessively long. */
        .hero-section {
          height: 230vh;
        }
        @media (max-width: 767px) {
          .hero-section {
            height: 130vh;
          }
        }
        /* Closed-state (p=0) arch geometry for SSR / first paint. The scroll
           effect overrides these inline as the portal opens. Desktop is a
           narrow, tall portal; mobile widens and flattens the arch so it
           doesn't pinch into a tight gothic spike on a phone. The scroll effect
           interpolates continuously across viewport widths, so these two
           breakpoints only approximate the first frame before JS runs.

           The --hero-* vars size the translucent frame + layered glow so the
           decoration scales with the viewport (clamped so it never gets too
           thin on large displays or too heavy on phones) instead of the old
           fixed pixel weights that distorted the framing on small screens. */
        .hero-arch {
          width: 58%;
          height: 70%;
          border-top-left-radius: 50% 72%;
          border-top-right-radius: 50% 72%;
          --hero-frame: clamp(4px, 0.7vw, 8px);
          --hero-band: clamp(7px, 1.6vw, 16px);
          --hero-blur: clamp(18px, 3.4vw, 34px);
          --hero-blur-spread: clamp(9px, 1.8vw, 18px);
          --hero-lift-y: clamp(12px, 2.4vw, 24px);
          --hero-lift-blur: clamp(32px, 7vw, 70px);
        }
        @media (max-width: 767px) {
          .hero-arch {
            width: 90%;
            height: 78%;
            border-top-left-radius: 50% 32%;
            border-top-right-radius: 50% 32%;
          }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        .hero-title-glow {
          text-shadow:
            0 0 12px rgba(255, 255, 255, 0.35),
            0 0 30px rgba(255, 255, 255, 0.18);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
